import React, { useEffect, useMemo, useState } from 'react';
import HeaderMainPage from '../../header/HeaderMainPage';
import Footer from '../homepage/Footer';
import { CheckCircle, Sparkles, Rocket, Zap, Building2, Dot, Loader2 } from 'lucide-react';

/**
 * BillingPage — API-mapped GBP pricing + safe checkout by price_id
 * ------------------------------------------------------------------
 * - Pulls Starter/Growth/Pro GBP amounts from API where available
 * - Falls back to local pricingData if API is missing/empty
 * - Checkout posts the exact price_id (no brittle array indexing)
 * - Enterprise remains "Contact Sales"
 * - Robust duplicate handling (prefers lower one-time amount)
 * - Button re-enables after any backend/Stripe error
 * ------------------------------------------------------------------
 */

// Shared credit usage rules for Starter, Growth, Pro
const CREDIT_FEATURES = [
  {
    title: 'Fetch Prospects / Leads',
    subtitle: '0.5 credit per prospect',
  },
  {
    title: 'Buying Intent Score',
    subtitle: '2 credits per prospect',
  },
  {
    title: 'Drafts (Email & LinkedIn)',
    subtitle:
      '1 credit per prospect (Conservative: 4 credits total for 1 initial + 3 follow-ups across email + LinkedIn)',
  },
  {
    title: 'Voice Notes',
    subtitle: '3 credits per prospect',
  },
  {
    title: 'Proposals',
    subtitle: '5 credits per prospect',
  },
];

const pricingData = {
  currencies: [
    { id: 'USD', symbol: '$', name: 'USA (USD)', flag: '🇺🇸' },
    { id: 'GBP', symbol: '£', name: 'UK (GBP)', flag: '🇬🇧' },
    { id: 'CAD', symbol: 'C$', name: 'Canada (CAD)', flag: '🇨🇦' },
    { id: 'AUD', symbol: 'A$', name: 'Australia (AUD)', flag: '🇦🇺' },
    { id: 'INR', symbol: '₹', name: 'India (INR)', flag: '🇮🇳' },
  ],
  plans: [
    {
      id: 1,
      title: 'Starter',
      credits: '500',
      tagline: 'Test autopilot on a focused batch of prospects.',
      // prices: { INR: '3,499', USD: '49', GBP: '39.99', CAD: '69.99', AUD: '79.99' },
      vat: { GBP: true },
      features: [],
      mixExample: {
        title: 'Autopilot coverage (approx prospects):',
        items: [
          'Conservative – 108 prospects without buying intent, 76 with buying intent',
          'Balanced – 52 prospects without buying intent, 43 with buying intent',
          'Aggressive – 52 prospects without buying intent, 43 with buying intent',
        ],
      },
      recommended: false,
      icon: 'sparkles',
      accent: 'from-violet-500/70 via-indigo-500/70 to-sky-500/70',
    },
    {
      id: 2,
      title: 'Growth',
      credits: '1,000',
      tagline: 'Scale your outbound with consistent weekly volume.',
      // prices: { INR: '6,999', USD: '99', GBP: '79.99', CAD: '139.99', AUD: '159.99' },
      vat: { GBP: true },
      features: [],
      mixExample: {
        title: 'Autopilot coverage (approx prospects):',
        items: [
          'Conservative – 216 prospects without buying intent, 152 with buying intent',
          'Balanced – 104 prospects without buying intent, 86 with buying intent',
          'Aggressive – 104 prospects without buying intent, 86 with buying intent',
        ],
      },
      recommended: true,
      icon: 'rocket',
      accent: 'from-indigo-500/80 via-blue-500/80 to-sky-500/80',
    },
    {
      id: 3,
      title: 'Pro',
      credits: '2,000',
      tagline: 'Run multiple campaigns and channels at full throttle.',
      // prices: { INR: '13,999', USD: '199', GBP: '159.99', CAD: '279.99', AUD: '319.99' },
      vat: { GBP: true },
      features: [],
      mixExample: {
        title: 'Autopilot coverage (approx prospects):',
        items: [
          'Conservative – 432 prospects without buying intent, 304 with buying intent',
          'Balanced – 208 prospects without buying intent, 172 with buying intent',
          'Aggressive – 208 prospects without buying intent, 172 with buying intent',
        ],
      },
      recommended: false,
      icon: 'zap',
      accent: 'from-sky-500/80 via-cyan-500/80 to-emerald-500/80',
    },
    {
      id: 4,
      title: 'Enterprise',
      credits: 'Custom',
      tagline: 'For teams that need custom credits, SLAs and support.',
      // prices: { INR: 'Custom', USD: 'Custom', GBP: 'Custom', CAD: 'Custom', AUD: 'Custom' },
      priceInfo: 'Starts at $499+',
      features: [
        'Custom Credit Amount',
        'Dedicated Account Manager',
        'API Access & Support',
        'Custom Onboarding & Training',
      ],
      recommended: false,
      icon: 'building',
      accent: 'from-slate-700/80 via-slate-800/80 to-slate-900/80',
    },
  ],
};

// Compare row data (simple ranges derived from the mixExample values)
const PLAN_COMPARISON = [
  {
    title: 'Starter',
    credits: '500',
    approxWithoutIntent: '52 – 108',
    approxWithIntent: '43 – 76',
  },
  {
    title: 'Growth',
    credits: '1,000',
    approxWithoutIntent: '104 – 216',
    approxWithIntent: '86 – 152',
  },
  {
    title: 'Pro',
    credits: '2,000',
    approxWithoutIntent: '208 – 432',
    approxWithIntent: '172 – 304',
  },
];

// Optional legacy mapping retained (not used for checkout anymore)
const CHECKOUT_INDEX = { Starter: 1, Growth: 2, Pro: 3, Enterprise: 4 };

// ---- helpers ----
const ICON_MAP = {
  sparkles: Sparkles,
  rocket: Rocket,
  zap: Zap,
  building: Building2,
};

const getChipColorClasses = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('conservative')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (l.includes('balanced')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  if (l.includes('aggressive')) return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
};

// ---------- UI bits ----------
function SkeletonCard() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="w-[290px] md:w-[300px] rounded-2xl border border-slate-200 bg-white shadow-lg animate-pulse p-6"
    >
      <div className="h-8 w-2/3 rounded-full bg-slate-200" />
      <div className="mt-3 h-6 w-1/2 rounded-full bg-slate-200" />
      <div className="mt-6 h-[180px] rounded-xl bg-slate-100" />
      <div className="mt-6 h-11 w-full rounded-full bg-slate-200" />
    </div>
  );
}

function TierIcon({ name, className = 'h-5 w-5' }) {
  const Icon = ICON_MAP[name] || Dot;
  return <Icon className={className} />;
}

function CurrencyPills({ selected, onSelect }) {
  const count = pricingData.currencies.length;
  const activeIdx = Math.max(0, pricingData.currencies.findIndex((c) => c.id === selected));
  const widthCss = `calc((100% - 1rem) / ${count})`;
  const leftCss = `calc(${activeIdx} * ((100% - 1rem) / ${count}) + 0.5rem)`;

  return (
    <div className="mt- sm:mt-8 flex justify-center px-3 sm:px-0">
      <div className="relative w-full max-w-3xl select-none">
        <div className="relative bg-red-600 rounded-full border border-slate-200 bg-white/80 shadow-[0_10px_40px_rgba(15,23,42,0.08)] backdrop-blur-md px-2 py-2">
          <div
            aria-hidden
            className="absolute top-1.5 bottom-1.5 rounded-full bg-slate-100 shadow-md ring-1 ring-slate-200 transition-all duration-300 ease-out"
            style={{ width: widthCss, left: leftCss }}
          />
          <div className="relative grid grid-cols-5">
            {pricingData.currencies.map((currency) => {
              const isActive = selected === currency.id;
              const name = currency.name;
              const country = name.split(' ')[0];
              const code = name.match(/\((.*?)\)/)?.[1] || '';
              return (
                <button
                  key={currency.id}
                  onClick={() => onSelect(currency.id)}
                  className="relative z-10 w-full flex items-center justify-center gap-2 rounded-full sm:px-3 py-2 sm:py-2.5 transition-colors duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300/50"
                  aria-pressed={isActive}
                  aria-label={`${country} (${code})`}
                >
                  <span
                    className={[
                      'flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full border text-lg shadow-sm',
                      isActive ? 'bg-white border-slate-300' : 'bg-slate-50 border-slate-200',
                    ].join(' ')}
                  >
                    {currency.flag}
                  </span>
                  <span className="hidden sm:block text-left leading-tight">
                    <span
                      className={[
                        'block text-[11px] font-medium uppercase ',
                        isActive ? 'text-slate-900' : 'text-slate-700',
                      ].join(' ')}
                    >
                      {country}
                    </span>
                    <span
                      className={[
                        'block text-[11px] font-medium uppercase',
                        isActive ? 'text-indigo-600' : 'text-slate-400',
                      ].join(' ')}
                    >
                      {code}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute -top-3 left-10 right-10 h-6 rounded-full bg-indigo-100/70 blur-xl"
        />
      </div>
    </div>
  );
}

function Card({ plan, selectedCurrency, displayPrice, vatApplies, onBuy, canCheckout, isBusy }) {
  const symbol = pricingData.currencies.find((c) => c.id === selectedCurrency)?.symbol || '';
  const isCustom = String(displayPrice).toLowerCase() === 'custom';

  return (
    <div
      className={`relative w-full rounded-2xl p-6 transition-all duration-300 flex flex-col h-full border bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)] hover:-translate-y-1 ring-1 ${plan.recommended
        ? 'border-indigo-500 ring-indigo-200 hover:ring-indigo-300'
        : 'border-slate-200 ring-slate-200/70 hover:border-slate-300'
        }`}
    >
      <div
        className={`pointer-events-none absolute -top-10 right-0 h-24 w-24 rounded-full blur-3xl opacity-40 bg-gradient-to-br ${plan.accent}`}
      />
      {plan.recommended && (
        <p className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-fuchsia-500 text-white text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
          Most Popular
        </p>
      )}

      <div className="text-center relative z-10">
        <div className="mx-auto mb-4 inline-flex items-center justify-center rounded-2xl px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700 shadow-sm">
          <TierIcon name={plan.icon} className="h-5 w-5 mr-1.5 text-indigo-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {plan.title === 'Enterprise' ? 'Custom for teams' : 'Credits for autopilot'}
          </span>
        </div>
        <h3 className="text-[1.4rem] font-extrabold tracking-tight text-slate-900">{plan.title}</h3>
        {plan.tagline && (
          <p className="mt-1 text-xs text-slate-500 max-w-xs mx-auto">{plan.tagline}</p>
        )}
        <p className="mt-4 text-4xl font-black tabular-nums text-slate-900">
          {!isCustom && symbol}
          {displayPrice}
          {vatApplies && (
            <span className="ml-1 align-super text-xs font-medium text-slate-400 tracking-tight">
              + VAT
            </span>
          )}
        </p>
        <p className="mt-1 text-sm font-semibold text-indigo-600">
          {plan.credits}{' '}
          <span className="text-slate-500 font-normal">
            credits •{' '}
            {plan.title === 'Enterprise'
              ? 'custom volume'
              : plan.title === 'Starter'
                ? 'ideal for early testing'
                : plan.title === 'Growth'
                  ? 'for consistent monthly outreach'
                  : 'for heavy multi-campaign use'}
          </span>
        </p>
      </div>

      {/* Per-plan feature list (kept for Enterprise only) */}
      {Array.isArray(plan.features) && plan.features.length > 0 && (
        <div className="mt-6 space-y-2 border-t border-slate-200 pt-5 text-left">
          {plan.features.map((feat, idx) => (
            <p key={idx} className="flex items-start">
              {plan.title === 'Enterprise' ? (
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
              ) : (
                <Dot className="h-4 w-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
              )}
              <span className="text-slate-700 text-[0.9rem] leading-6">{feat}</span>
            </p>
          ))}
        </div>
      )}

      {plan.mixExample && (
        <div className="mt-6 space-y-3 border-t border-slate-200 pt-5 text-left">
          <p className="font-semibold text-slate-800 text-xs flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 border border-indigo-100">
              <Rocket className="h-3 w-3 text-indigo-500" />
            </span>
            {plan.mixExample.title}
          </p>
          <div className="space-y-2">
            {plan.mixExample.items.map((item, idx) => {
              const [label, rest] = item.split('–');
              const labelText = label?.trim() || '';
              const restText = rest ? rest.trim() : '';
              return (
                <div
                  key={idx}
                  className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
                >
                  {labelText && (
                    <span
                      className={
                        'inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ' +
                        getChipColorClasses(labelText)
                      }
                    >
                      {labelText}
                    </span>
                  )}
                  <span className="text-[11px] text-slate-600 leading-5">
                    {rest ? `– ${restText}` : item}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {plan.priceInfo && (
        <p className="text-left text-xs text-slate-500 mt-4">{plan.priceInfo}</p>
      )}

      <div className="mt-auto pt-7 relative z-10">
        {isCustom ? (
          <a
            href="mailto:sales@roboman.ai"
            className="w-full inline-flex items-center justify-center gap-2 text-center py-3 px-6 rounded-lg font-semibold transition-all duration-300 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white hover:bg-black hover:shadow-[0_16px_40px_rgba(15,23,42,0.35)]"
          >
            <Building2 className="h-4 w-4" /> Contact Sales
          </a>
        ) : (
          <button
            onClick={onBuy}
            // disabled={!canCheckout || isBusy}
            className={`w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${!canCheckout || isBusy
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
              : plan.recommended
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-[0_16px_40px_rgba(79,70,229,0.35)]'
                : 'bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-slate-50 border border-violet-600 hover:bg-violet-200 hover:scale-50]'
              }`}
            title={!canCheckout ? 'Checkout not available for this currency yet' : undefined}
          >
            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {isBusy ? 'Processing…' : 'Buy Credits'}
          </button>
        )}

        {!isCustom && (
          <p className="mt-2 text-[11px] text-slate-500 text-center">
            One-time purchase • No subscription • Credits never auto-renew
          </p>
        )}
      </div>
    </div>
  );
}

function CreditUsageSection() {
  return (
    <section className="mt-12 mb-4">
      <div className="max-w-4xl mx-auto rounded-2xl border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)] px-6 py-5 md:px-8 md:py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xs font-semibold text-indigo-600 tracking-[0.16em] uppercase">
              How credits are used
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-slate-600">
              The same credit rules apply to Starter, Growth and Pro. Spend credits flexibly across
              channels and campaigns.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700 border border-emerald-100">
            <Rocket className="h-3.5 w-3.5" />
            <span>Multi-channel, intent-aware outreach</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CREDIT_FEATURES.map((item, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 rounded-xl bg-slate-50 px-3 py-3 border border-slate-100"
            >
              <div className="mt-0.5">
                <Zap className="h-4 w-4 text-indigo-500" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-slate-900">{item.title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{item.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanComparisonTable() {
  return (
    <section className="mt-12">
      <div className="max-w-4xl mx-auto rounded-2xl border border-slate-200 bg-white shadow-sm px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Compare plans at a glance</h3>
            <p className="text-xs text-slate-500">
              Approximate number of prospects you can cover with each credit pack.
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 border border-slate-200">
            <Sparkles className="h-3 w-3 text-indigo-500" />
            <span>Guidance, not hard limits</span>
          </span>
        </div>

        <div className="hidden md:block">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4 font-medium">Plan</th>
                <th className="py-2 pr-4 font-medium">Credits</th>
                <th className="py-2 pr-4 font-medium">Approx prospects (without intent)</th>
                <th className="py-2 pr-4 font-medium">Approx prospects (with intent)</th>
              </tr>
            </thead>
            <tbody>
              {PLAN_COMPARISON.map((row) => (
                <tr
                  key={row.title}
                  className="border-b border-slate-100 last:border-0 text-slate-700"
                >
                  <td className="py-2 pr-4 text-sm font-semibold">{row.title}</td>
                  <td className="py-2 pr-4 text-sm">{row.credits}</td>
                  <td className="py-2 pr-4 text-sm">
                    <span className="inline-flex w-44 items-center justify-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 border border-slate-200">
                      {row.approxWithoutIntent} prospects
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-sm">
                    <span className="inline-flex w-44 items-center justify-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 border border-slate-200">
                      {row.approxWithIntent} prospects
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-3">
          {PLAN_COMPARISON.map((row) => (
            <div
              key={row.title}
              className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{row.title}</p>
                  <p className="text-[11px] text-slate-500">{row.credits} credits pack</p>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-slate-500">Without intent</span>
                  <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 font-medium text-slate-700 border border-slate-200">
                    {row.approxWithoutIntent} prospects
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-slate-500">With intent</span>
                  <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 font-medium text-slate-700 border border-slate-200">
                    {row.approxWithIntent} prospects
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Page ----------
export default function PricingPage() {
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;
  const main_website = process.env.REACT_APP_MAIN_WEBSITE;

  const [stripePrices, setStripePrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('GBP'); // default view

  // checkout UI states
  const [busyPriceId, setBusyPriceId] = useState('');
  const [toast, setToast] = useState(null);

  // Fetch prices
  useEffect(() => {
    const getStripePrices = async () => {
      setFetchError(null);

      if (!roboman_api) {
        setFetchError('API base URL (REACT_APP_ROBOMAN_API) is not configured.');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        const headers = { Accept: 'application/json' };
        if (token) headers['Authorization'] = token;

        const response = await fetch(`${roboman_api}/stripe/prices`, { method: 'GET', headers });
        const data = await response.json();

        if (!response.ok) {
          console.error('Request failed:', data);
          setFetchError('Unable to load pricing right now.');
          setStripePrices([]);
        } else {
          setStripePrices(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Network or parsing error:', error);
        setFetchError('Network error while fetching pricing.');
        setStripePrices([]);
      } finally {
        setLoading(false);
      }
    };

    getStripePrices();
    document.body.classList.add('overflow-x-hidden');
    return () => document.body.classList.remove('overflow-x-hidden');
  }, [roboman_api]);

  // Clear busy state when currency changes
  useEffect(() => {
    setBusyPriceId('');
  }, [selectedCurrency]);

  // Build a quick lookup: `${plan}|${CURRENCY}` -> price object
  // const apiPriceMap = useMemo(() => {
  //   if (!stripePrices.length) return {};
  //   const map = {};
  //   for (const p of stripePrices) {
  //     const currency = (p.price_currency || '').toString().toUpperCase();

  //     if (currency !== 'GBP') continue; // wiring GBP per requirement

  //     // Accept all supported currencies from the API
  //     // const supportedCurrencies = ['GBP', 'USD', 'CAD', 'AUD', 'INR'];
  //     // if (!supportedCurrencies.includes(currency)) continue;

  //     const blob = `${p.price_name || ''} ${p.price_description || ''}`.toLowerCase();
  //     let planKey = null;
  //      // Match based on credit amounts in description or plan names
  //     if (blob.includes('starter')|| blob.includes('500')) planKey = 'starter';
  //     else if (blob.includes('growth')|| blob.includes('scale') ||  blob.includes('1000')) planKey = 'growth';
  //     else if (blob.includes('pro')|| blob.includes('2000')) planKey = 'pro';


  //     if (!planKey) continue;

  //     const key = `${planKey}|${currency}`;
  //     const amount = Number(p.price_amount);
  //     if (!Number.isFinite(amount)) continue;

  //     if (!map[key] || amount < Number(map[key].price_amount)) {
  //       map[key] = p; // keep the cheaper one
  //     }
  //   }
  //   return map;
  // }, [stripePrices]);

  const apiPriceMap = useMemo(() => {
    const map = {};
    stripePrices.forEach((p) => {
      const currency = p.price_currency.toUpperCase();

      let planKey = null;
      if (p.price_description?.includes('500')) planKey = 'Starter';
      else if (p.price_description?.includes('1000')) planKey = 'Growth';
      else if (p.price_description?.includes('2000')) planKey = 'Pro';

      if (!planKey) return;

      if (!map[planKey]) map[planKey] = {};
      map[planKey][currency] = p;
    });

    return map;
  }, [stripePrices]);


  // Resolve display price (string/number) for a plan & currency
  const resolveDisplayPrice = (planTitle, currency) => {
    const key = `${planTitle.toLowerCase()}|${currency}`;
    const apiHit = apiPriceMap[key];
    if (apiHit && apiHit.price_amount != null) {
      return String(apiHit.price_amount);
    }
    // Fallback to local static table
    const localPlan = pricingData.plans.find((p) => p.title === planTitle);
    return localPlan?.prices?.[currency] ?? 'Custom';
  };

  // Resolve API price_id for checkout (only when we have a mapped API entry)
  const resolvePriceId = (planTitle, currency) => {
    const key = `${planTitle.toLowerCase()}|${currency}`;
    const apiHit = apiPriceMap[key];
    return apiHit?.price_id || null;
  };

  // Create checkout session
  const createPaymentSession = async (priceId) => {
    try {
      if (!roboman_api || !priceId) return;

      setBusyPriceId(priceId);
      setToast(null);

      const token = localStorage.getItem('access_token');
      const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = token;

      const response = await fetch(`${roboman_api}/stripe/checkout/session/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          price_id: priceId,
          cancel_url: `${main_website}dashboard`,
          success_url: `${main_website}successpayment?session_id={CHECKOUT_SESSION_ID}`,
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        /* ignore non-JSON */
      }

      if (!response.ok) {
        const msg =
          (data && (data.detail || data.message || data.error)) ||
          'Checkout failed. Please try again.';
        setToast({ type: 'error', msg });
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setToast({ type: 'error', msg: 'Network error while creating checkout session.' });
    } finally {
      setBusyPriceId('');
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="relative z-30">
        <HeaderMainPage />
      </div>

      {/* Content */}
      <main className="relative z-10 py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {toast?.type === 'error' && (
            <div className="text-center mb-6">
              <div className="mx-auto max-w-xl rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800 shadow">
                {toast.msg}
              </div>
            </div>
          )}

          {/* Hero */}
          <div className="text-center mb-10 sm:mb-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-50">
                <Sparkles className="h-3 w-3 text-indigo-500" />
              </span>
              <span>Credits-based Autopilot for B2B Outreach</span>
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight font-Inter opacity-1 bg-gradient-to-r from-gray-900 via-blue-800 to-fuchsia-800  bg-clip-text text-transparent">
              Flexible Credit Packages
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-slate-500">
              Use credits across email, LinkedIn, proposals and voice notes. Turn on Conservative,
              Balanced or Aggressive autopilot — with or without buying intent scoring.
            </p>
            <CurrencyPills selected={selectedCurrency} onSelect={setSelectedCurrency} />
          </div>

          {/* Loading */}
          {loading && (
            <div className="grid lg:grid-cols-4 gap-8 items-start justify-items-center">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {/* Error */}
          {!loading && fetchError && (
            <div className="mx-auto max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow">
              {fetchError}
            </div>
          )}

          {/* Plans, then comparison, then "How credits are used" at the bottom */}
          {!loading && !fetchError && (
            <>
              <div className="grid lg:grid-cols-4 gap-7 items-stretch">
                {pricingData.plans.map((plan) => {

                  const isEnterprise = plan.title === 'Enterprise';

                  const displayPrice = isEnterprise ? 'Custom' : apiPriceMap?.[plan.title]?.[selectedCurrency]?.price_amount ?? '—';
                  // const displayPrice = resolveDisplayPrice(plan.title, selectedCurrency);
                  const vatApplies = !!(plan.vat && plan.vat[selectedCurrency]);
                  // const priceId = resolvePriceId(plan.title, selectedCurrency);
                  const priceId = isEnterprise ? null : apiPriceMap?.[plan.title]?.[selectedCurrency]?.price_id ?? null;

                  // const canCheckout = plan.title !== 'Enterprise' && selectedCurrency === 'GBP' && !!priceId;
                  const canCheckout = !isEnterprise && !!priceId;


                  return (
                    <Card
                      key={plan.id}
                      plan={plan}
                      selectedCurrency={selectedCurrency}
                      displayPrice={displayPrice}
                      vatApplies={vatApplies}
                      canCheckout={canCheckout}
                      isBusy={Boolean(busyPriceId) && busyPriceId === priceId}
                      onBuy={() => {
                        if (!canCheckout) {
                          console.warn(
                            `No API price_id for ${plan.title} in ${selectedCurrency}`,
                          );
                          return;
                        }
                        createPaymentSession(priceId);
                      }}
                    />
                  );
                })}
              </div>

              {/* Compare at a glance */}
              <PlanComparisonTable />

              {/* How credits are used — kept at the bottom after compare plans */}
              <CreditUsageSection />
            </>
          )}
        </div>
      </main>

      {/* Footer (optional) */}
      {/* <div className="relative z-10">
        <Footer />
      </div> */}
    </div>
  );
}

/*
  ==============================================================
  TESTS (Jest + React Testing Library) — unchanged API mock passes
  Save as: BillingPage.test.jsx
  ==============================================================

  import React from 'react';
  import { render, screen, waitFor, fireEvent } from '@testing-library/react';
  import BillingPage from './BillingPage';

  jest.mock('../../header/HeaderMainPage', () => () => <div>Header</div>);
  jest.mock('../homepage/Footer', () => () => <div>Footer</div>);

  describe('BillingPage', () => {
    beforeEach(() => {
      jest.resetAllMocks();
      global.fetch = jest.fn(() => Promise.resolve({
        ok: true,
        json: () => Promise.resolve([]), // empty -> fallback to local prices
      }));
    });

    test('renders without Router and shows skeletons', () => {
      render(<BillingPage />);
      expect(screen.getAllByRole('status', { hidden: true }).length).toBeGreaterThan(0);
    });

    test('currency pills switch price labels (fallback)', async () => {
      render(<BillingPage />);
      await waitFor(() => expect(fetch).toHaveBeenCalled());
      fireEvent.click(screen.getByRole('button', { name: /UK \(GBP\)/i }));
      expect(screen.getByText('79.99')).toBeInTheDocument(); // Growth GBP fallback
    });
  });
*/
